import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Play, Repeat2 } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui';
import { STAT_CARD_BASE } from '../../components/ui/StatCard';
import microSimService from '../../services/microSimService';
import { useMicroSimPaths } from '../../utils/microSimPaths';
import { showError } from '../../utils/toast';

/**
 * One micro simulation: what it practises, how it runs, and "Preview as a pair" —
 * the trainer plays both seats to see the conversation. Nothing from a preview is
 * saved to any report.
 */
const MicroSimDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const paths = useMicroSimPaths();
  const [sim, setSim] = useState(null);
  const [industry, setIndustry] = useState('');
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let alive = true;
    microSimService.get(slug)
      .then((res) => {
        if (!alive) return;
        setSim(res.microSim);
        setIndustry(res.microSim.industries[0]?.key || '');
      })
      .catch((err) => {
        showError(err.userMessage || 'Could not load this micro simulation');
        navigate(paths.library);
      });
    return () => { alive = false; };
  }, [slug, navigate, paths.library]);

  const preview = async () => {
    setStarting(true);
    try {
      const res = await microSimService.startPreview(slug, { industry });
      navigate(paths.preview(slug, res.runId));
    } catch (err) {
      showError(err.userMessage || 'Could not start the preview');
      setStarting(false);
    }
  };

  if (!sim) {
    return (
      <DashboardLayout title="Micro simulation">
        <div className="mx-auto max-w-4xl px-4 py-6"><div className={`${STAT_CARD_BASE} h-48 animate-pulse`} /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Micro simulation">
      <div className="mx-auto w-full max-w-4xl space-y-5 px-4 py-6 lg:px-6">
        <button
          type="button"
          onClick={() => navigate(paths.library)}
          className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-800 dark:text-neutral-400"
        >
          <ArrowLeft size={15} /> Library
        </button>

        <div className={`${STAT_CARD_BASE} p-5 sm:p-6`}>
          <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700 ring-1 ring-inset ring-violet-100">
            {sim.theme}
          </span>
          <h2 className="mt-3 text-2xl font-bold text-neutral-900 dark:text-neutral-100">{sim.title}</h2>
          {sim.subtitle && <p className="mt-1 text-neutral-500 dark:text-neutral-400">{sim.subtitle}</p>}
          {sim.summary && <p className="mt-3 text-sm leading-relaxed text-neutral-700 dark:text-neutral-300">{sim.summary}</p>}

          <div className="mt-5 flex flex-wrap items-end gap-3 border-t border-neutral-200 pt-5 dark:border-neutral-800">
            <label className="text-sm">
              <span className="block text-xs font-semibold uppercase tracking-wide text-neutral-500">Industry version</span>
              {/* pr-10: the forms plugin draws the arrow inside the box on the right, so
                  equal padding put it on top of the text. h-10 matches the button beside it. */}
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="mt-1.5 h-10 min-w-[13rem] rounded-xl border border-neutral-200 bg-white py-0 pl-3.5 pr-10 text-sm text-neutral-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:focus:ring-primary-500/20"
              >
                {sim.industries.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
              </select>
            </label>
            <Button variant="primary-filled" onClick={preview} loading={starting}>
              <Play size={15} className="mr-1.5" /> Preview as a pair
            </Button>
          </div>
          <p className="mt-2 text-xs text-neutral-400">
            You play both people. To run it with participants, open a live session and use “Run a micro simulation”.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className={`${STAT_CARD_BASE} p-5`}>
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">How it runs</h3>
            <ul className="mt-3 space-y-2.5">
              {sim.rounds.map((r) => (
                <li key={r.index} className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-xs font-bold text-primary-700">
                    {r.index + 1}
                  </span>
                  <span className="text-sm">
                    <span className="font-medium text-neutral-900 dark:text-neutral-100">{r.title}</span>
                    <span className="block text-xs text-neutral-500">
                      {r.turns} turns · {r.opensWith === 'A' ? 'Person A' : 'Person B'} opens as {sim.roles.initiator}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-3 flex items-center gap-1.5 text-xs text-neutral-500">
              <Repeat2 size={13} /> Roles swap between rounds: {sim.roles.initiator} ↔ {sim.roles.responder}
            </p>
          </div>

          <div className={`${STAT_CARD_BASE} p-5`}>
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Skills it measures</h3>
            <ul className="mt-3 space-y-2">
              {sim.skills.map((s) => (
                <li key={s.key} className="text-sm">
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">{s.label}</span>
                  {s.description && <span className="block text-xs text-neutral-500">{s.description}</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {(sim.learningObjectives.length > 0 || sim.debriefQuestions.length > 0) && (
          <div className="grid gap-4 md:grid-cols-2">
            {sim.learningObjectives.length > 0 && (
              <div className={`${STAT_CARD_BASE} p-5`}>
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Participants will practise</h3>
                <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-neutral-700 dark:text-neutral-300">
                  {sim.learningObjectives.map((o) => <li key={o}>{o}</li>)}
                </ul>
              </div>
            )}
            {sim.debriefQuestions.length > 0 && (
              <div className={`${STAT_CARD_BASE} p-5`}>
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Debrief questions</h3>
                <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-neutral-700 dark:text-neutral-300">
                  {sim.debriefQuestions.map((q) => <li key={q}>{q}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MicroSimDetailPage;
